import { Container, Title, Text, Paper, Stack, Group, useMantineTheme, Box, Card, ThemeIcon, Button, SimpleGrid, Badge } from '@mantine/core';
import { IconHeartFilled, IconCalendarEvent, IconArrowRight, IconCurrencyKroneSwedish } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import { treatmentsData } from '../utils/treatmentData';
import { SEOHead } from '../components/SEOHead';
import { FEATURE_TOGGLES } from '../utils/featureToggles';
import { useEffect } from 'react';

export function TreatmentsPage() {
  const theme = useMantineTheme();
  const showPricing = FEATURE_TOGGLES.SHOW_TREATMENT_CARD_PRICING;

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  return (
    <>
      <SEOHead 
        title="Vælg din akupunktur behandlinger | Bestil tid online"
        description="Find den rette akupunkturbehandling i Horsens. Hos Lykke Terapien får du skræddersyede forløb, der fremmer balance, ro og sundhed."
        canonical="/behandlinger"
      />
      <Container size="xl" py="xl" pt={50} mx="auto" className="treatments-page-container">
      <Paper 
        p="xl" 
        radius="md" 
        withBorder 
        shadow="sm"
        style={{
          background: 'rgba(255, 255, 255, 0.8)',
          borderColor: theme.colors.gold[2]
        }}
        className="treatments-main-paper"
      >
        {/* Header */}
        <Group mb="xl" align="center" className="treatments-header">
          <IconHeartFilled size={32} style={{ color: theme.colors.gold[6] }} />
          <Title order={1} c={theme.colors.gold[7]} className="treatments-main-title">
            Behandlinger
          </Title>
        </Group>

        <Text size="lg" c="gray.7" mb="md" lh={1.6} className="treatments-intro">
          Udforsk klinikkens omfattende behandlingstilbud baseret på klassisk kinesisk medicin med en rolig og nærværende tilgang.
          Hver behandling er individuelt tilpasset dine behov for optimal balance og velvære.
        </Text>

        {/* Pricing Information */}
        <Paper 
          p="lg" 
          radius="md" 
          mb="xl"
          style={{
            background: `linear-gradient(135deg, ${theme.colors.gold[0]} 0%, ${theme.colors.gold[1]} 100%)`,
            border: `2px solid ${theme.colors.gold[3]}`
          }}
          className="treatments-pricing"
        >
          <Group justify="center" align="center" gap="lg" wrap="wrap">
            <Group align="center" gap="sm">
              <Box>
                <Text size="lg" fw={600} c={theme.colors.dark[7]}>
                  Aktuelle priser
                </Text>
                <Text size="sm" c="gray.6">
                  Særtilbud på alle behandlinger
                </Text>
              </Box>
            </Group>
            <Group gap="md" wrap="wrap" justify="center" className="price-badges-container">
              <Badge
                size="xl"
                color="gold"
                variant="filled"
                className="price-badge price-badge-main"
              >
                Alle behandlinger: 450 kr
              </Badge>
              <Badge
                size="xl"
                color="blue"
                variant="filled"
                className="price-badge price-badge-nada"
              >
                NADA: 150 kr
              </Badge>
            </Group>
          </Group>
        </Paper>

        {/* Treatment Cards Grid */}
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg" mb="xl" className="treatments-grid">
          {treatmentsData.map((treatment) => {
            const TreatmentIcon = treatment.icon;
            // Fallback for colors that might not exist in theme
            const safeColor = theme.colors[treatment.color] ? treatment.color : 'blue';
            
            return (
              <Card
                key={treatment.id}
                shadow="sm"
                padding="lg"
                radius="md"
                withBorder
                component={Link}
                to={`/behandlinger/${treatment.slug}`}
                style={{
                  textDecoration: 'none',
                  color: 'inherit',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  background: `linear-gradient(135deg, ${theme.colors[safeColor][0]} 0%, ${theme.colors[safeColor][1]} 100%)`,
                  borderColor: theme.colors[safeColor][2]
                }}
                className="treatment-card"
              >
                <Stack gap="md" h="100%">
                  {/* Icon */}
                  <ThemeIcon 
                    size="xl" 
                    radius="50%" 
                    color={safeColor} 
                    variant="light"
                    className="treatment-card-icon"
                  >
                    <TreatmentIcon size={28} />
                  </ThemeIcon>

                  {/* Content */}
                  <Box flex={1}>
                    <Title order={3} size="h4" mb="xs" c={theme.colors.dark[7]} className="treatment-card-title">
                      {treatment.title}
                    </Title>
                    <Text size="sm" c="gray.6" lh={1.5} className="treatment-card-description">
                      {treatment.shortDescription}
                    </Text>
                  </Box>

                  {/* Price Badge */}
                  {showPricing && (
                    <Badge
                      size="lg"
                      color={safeColor}
                      variant="filled"
                      style={{ alignSelf: 'flex-start' }}
                    >
                      {treatment.price} kr
                    </Badge>
                  )}

                  {/* Read More Button */}
                  <Group justify="space-between" align="center" mt="auto">
                    <Text size="sm" fw={500} c={theme.colors[safeColor][7]}>
                      Læs mere
                    </Text>
                    <IconArrowRight size={16} style={{ color: theme.colors[safeColor][6] }} />
                  </Group>
                </Stack>
              </Card>
            );
          })}
        </SimpleGrid>

        {/* Call to Action */}
        <Paper 
          p="xl" 
          radius="md" 
          style={{
            background: `linear-gradient(135deg, ${theme.colors.gold[1]} 0%, ${theme.colors.gold[0]} 100%)`,
            borderColor: theme.colors.gold[3]
          }}
          withBorder
          className="treatments-cta"
        >
          <Stack align="center" gap="md">
            <Button
              component="a"
              href="https://lykke-terapien.planway.com/"
              target="_blank"
              rel="noopener noreferrer"
              size="lg"
              leftSection={<IconCalendarEvent size={18} />}
              className="treatments-cta-button"
            >
              Book en tid i dag
            </Button>
          </Stack>
        </Paper>

        {/* Footer Note */}
        <Paper 
          p="md" 
          mt="xl" 
          radius="md" 
          style={{ background: 'rgba(255, 248, 220, 0.3)' }} 
          className="treatments-footer"
        >
          <Text fs="italic" c="dimmed" ta="center" lh={1.6} fw={500} className="treatments-footer-text">
            Alle behandlinger tilpasses individuelt efter dine behov og tilstand.
          </Text>
        </Paper>
      </Paper>

      <style>{`
        /* Default badge styling - Desktop */
        .price-badge {
          padding: 0.75rem 1.5rem;
          font-size: 1rem;
          white-space: nowrap;
          text-align: center;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          line-height: 1.5;
          width: auto;
        }

        /* Mobile-first responsive design */
        @media (max-width: 768px) {
          .treatments-page-container {
            padding: 1rem !important;
            padding-top: 60px !important;
          }
          
          .treatments-main-paper {
            padding: 1.5rem !important;
          }
          
          .treatments-header {
            margin-bottom: 2rem !important;
            flex-direction: column !important;
            text-align: center !important;
          }
          
          .treatments-main-title {
            font-size: 1.75rem !important;
            text-align: center !important;
          }
          
          .treatments-intro {
            font-size: 1rem !important;
            margin-bottom: 1rem !important;
            text-align: center !important;
          }
          
          .treatments-pricing {
            padding: 1.5rem !important;
            margin-bottom: 2rem !important;
          }
          
          .treatments-pricing .mantine-Group-root {
            flex-direction: column !important;
            gap: 1rem !important;
          }

          .price-badges-container {
            flex-direction: column !important;
            width: 100% !important;
            gap: 0.75rem !important;
          }

          .price-badge {
            font-size: 0.875rem !important;
            padding: 0.75rem 1rem !important;
            white-space: normal !important;
            word-wrap: break-word !important;
            word-break: keep-all !important;
            overflow-wrap: break-word !important;
            text-overflow: clip !important;
            display: inline-block !important;
            width: auto !important;
            max-width: 90vw !important;
            height: auto !important;
            line-height: 1.5 !important;
            text-align: center !important;
          }
          
          .treatments-grid {
            gap: 1rem !important;
            margin-bottom: 2rem !important;
          }
          
          .treatment-card {
            padding: 1.5rem !important;
            min-height: 200px !important;
          }
          
          .treatment-card-icon {
            width: 60px !important;
            height: 60px !important;
            align-self: center !important;
          }
          
          .treatment-card-title {
            font-size: 1.125rem !important;
            text-align: center !important;
            margin-bottom: 0.5rem !important;
          }
          
          .treatment-card-description {
            font-size: 0.875rem !important;
            text-align: center !important;
            line-height: 1.4 !important;
          }
          
          .treatments-cta {
            padding: 1.5rem !important;
          }
          
          .treatments-cta-title {
            font-size: 1.25rem !important;
          }
          
          .treatments-cta-text {
            font-size: 0.9rem !important;
          }
          
          .treatments-cta-button {
            width: 100% !important;
            max-width: 280px !important;
          }
          
          .treatments-footer {
            padding: 1rem !important;
            margin-top: 2rem !important;
          }
          
          .treatments-footer-text {
            font-size: 0.875rem !important;
          }
        }
        
        /* Tablet improvements */
        @media (min-width: 769px) and (max-width: 1024px) {
          .treatments-page-container {
            padding: 1.5rem !important;
            padding-top: 70px !important;
          }
          
          .treatments-main-paper {
            padding: 2rem !important;
          }
          
          .treatments-main-title {
            font-size: 2rem !important;
          }
          
          .treatments-intro {
            font-size: 1.125rem !important;
          }
          
          .treatment-card {
            padding: 2rem !important;
            min-height: 250px !important;
          }
        }
        
        /* Enhanced card interactions */
        .treatment-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 25px rgba(0,0,0,0.15) !important;
        }
        
        .treatment-card:hover .treatment-card-icon {
          transform: scale(1.1);
        }
        
        /* Better grid layout */
        @media (max-width: 768px) {
          .treatments-grid .mantine-SimpleGrid-root {
            grid-template-columns: 1fr !important;
          }
        }
        
        @media (min-width: 769px) and (max-width: 1024px) {
          .treatments-grid .mantine-SimpleGrid-root {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        
        /* Enhanced visual hierarchy */
        .treatment-card {
          height: 100%;
          display: flex;
          flex-direction: column;
        }
        
        .treatment-card .mantine-Stack-root {
          height: 100%;
        }
        
        /* Better spacing */
        @media (max-width: 768px) {
          .treatments-header .mantine-Group-root {
            gap: 0.5rem !important;
          }
        }
        
        /* Enhanced focus states */
        @media (max-width: 768px) {
          .treatment-card:focus {
            outline: 2px solid ${theme.colors.blue[6]} !important;
            outline-offset: 2px !important;
          }
          
          .treatments-cta-button:focus {
            outline: 2px solid ${theme.colors.blue[6]} !important;
            outline-offset: 2px !important;
          }
        }
        
        /* Loading and animation improvements */
        .treatment-card {
          transition: all 0.3s ease !important;
        }
        
        .treatment-card-icon {
          transition: all 0.3s ease !important;
        }
        
        /* Better text contrast */
        .treatment-card-title {
          color: ${theme.colors.dark[7]} !important;
        }
        
        .treatment-card-description {
          color: ${theme.colors.gray[6]} !important;
        }
        
        /* Improved CTA styling */
        .treatments-cta-button:hover {
          transform: translateY(-2px);
          transition: all 0.2s ease;
        }
      `}</style>
      </Container>
    </>
  );
}